import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import React, { useMemo } from "react";

import { forceStartReplayAndGetLink } from "../sentry";
import { commandCopy, commandOpen } from "./commandPalette/commandBuilders";
import { templateLinearTicket, buildLinearNewIssueUrl } from "./commandPalette/linear";
import { useRegisterCommands, type Command } from "./CommandPaletteRegistry";
import { LinearIcon } from "./LinearIcon";

type DerivedContext = {
  tenantId?: string;
  encodedTenantId?: string;
  facilityId?: string;
  facilityDisplay?: string;
  encodedFacilityId?: string;
  reportId?: string;
  encodedReportId?: string;
};

export function DefaultCommandRegistrations({
  derived,
  tenantLabel,
  navigate,
  getAppBaseUrl,
  copyToClipboard,
  copyRichLink,
}: {
  derived: DerivedContext;
  tenantLabel: string;
  navigate: (path: string) => void;
  getAppBaseUrl: () => string;
  copyToClipboard: (t: string) => Promise<void> | void;
  copyRichLink: (u: string, l: string) => Promise<void> | void;
}): React.ReactElement | null {
  const hrefDep = typeof window !== "undefined" ? window.location.href : getAppBaseUrl();

  // "Copy link to current page" command (always available, registered first to appear at top)
  const rawTitle = document.title;
  const titlePrefix = "Encamp / ";
  const strippedTitle = rawTitle.startsWith(titlePrefix)
    ? rawTitle.slice(titlePrefix.length) || rawTitle
    : rawTitle;
  const pageLinkCmds = useMemo<Command[]>(
    () => [
      {
        id: "copy-page-link",
        label: "Copy link to current page",
        description: strippedTitle,
        type: "copy-link",
        keywords: ["copy", "page", "link", "url"],
        signature: `page-link:${hrefDep}:${strippedTitle}`,
        run: () => {
          const raw = document.title;
          const prefix = "Encamp / ";
          const label = raw.startsWith(prefix) ? raw.slice(prefix.length) || raw : raw;
          copyRichLink(window.location.href, label);
        },
      },
    ],
    [hrefDep, strippedTitle, copyRichLink]
  );

  useRegisterCommands(pageLinkCmds, [pageLinkCmds], "page-link");

  // Staff fulfillment navigation commands (always available)
  const staffNavCmds = useMemo<Command[]>(
    () => [
      commandOpen({
        id: "nav-fulfillment-facilities",
        label: "Go to Chemical Facilities",
        path: "/staff/fulfillment/facilities",
        navigate,
        description: "/staff/fulfillment/facilities",
        signature: "nav:fulfillment-facilities",
        keywords: ["fulfillment", "facilities", "chemical"],
      }),
      commandOpen({
        id: "nav-fulfillment-workflows",
        label: "Go to Workflows",
        path: "/staff/fulfillment/workflows",
        navigate,
        description: "/staff/fulfillment/workflows",
        signature: "nav:fulfillment-workflows",
        keywords: ["fulfillment", "workflows"],
      }),
      commandOpen({
        id: "nav-fulfillment-wait-for-all",
        label: "Go to Wait For All",
        path: "/staff/fulfillment/wait-for-all",
        navigate,
        description: "/staff/fulfillment/wait-for-all",
        signature: "nav:fulfillment-wait-for-all",
        keywords: ["fulfillment", "wait"],
      }),
      commandOpen({
        id: "nav-fulfillment-error-triage",
        label: "Go to Error Triage",
        path: "/staff/fulfillment/error-clustering",
        navigate,
        description: "/staff/fulfillment/error-clustering",
        signature: "nav:fulfillment-error-triage",
        keywords: ["fulfillment", "error", "triage", "clustering"],
      }),
      commandOpen({
        id: "nav-fulfillment-waste",
        label: "Go to Waste Submissions",
        path: "/staff/fulfillment/waste",
        navigate,
        description: "/staff/fulfillment/waste",
        signature: "nav:fulfillment-waste",
        keywords: ["fulfillment", "waste", "submissions"],
      }),
    ],
    [navigate]
  );

  useRegisterCommands(staffNavCmds, [staffNavCmds], "staff-nav");

  // Notifications navigation — available regardless of tenant context because
  // notifications are user-centric (the bell, inbox, and detail pages all live
  // at /notifications, gated by the FeatureFlagKey.Notifications flag at the
  // route level). Each tile-view also gets its own command so power users can
  // jump straight to the filtered list without two clicks.
  const notificationNavCmds = useMemo<Command[]>(
    () => [
      commandOpen({
        id: "nav-notifications",
        label: "Go to Notifications",
        path: "/notifications",
        navigate,
        description: "/notifications",
        signature: "nav:notifications",
        keywords: ["notifications", "inbox", "alerts", "bell"],
      }),
      commandOpen({
        id: "nav-notifications-aging",
        label: "Open Aging notifications",
        path: "/notifications?view=AGING",
        navigate,
        description: "Unread for more than 3 days, all organizations",
        signature: "nav:notifications:aging",
        keywords: ["notifications", "aging", "old", "stale"],
      }),
      commandOpen({
        id: "nav-notifications-unread",
        label: "Open Unread notifications",
        path: "/notifications?view=UNREAD",
        navigate,
        description: "Everything you have not opened, all organizations",
        signature: "nav:notifications:unread",
        keywords: ["notifications", "unread", "inbox"],
      }),
    ],
    [navigate]
  );

  useRegisterCommands(notificationNavCmds, [notificationNavCmds], "notifications-nav");

  // Customer Toolkit + Catalogs + Program-area commands. These are tenant-scoped
  // (they need an active /o/:tenantId to make sense). Gating mirrors the
  // customer left-nav so the palette doesn't dead-end into a feature the user
  // can't reach.
  const customerNavCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (!derived.encodedTenantId) {
      return items;
    }
    const tenantBase = `/o/${derived.encodedTenantId}`;

    items.push(
      commandOpen({
        id: "nav-tasks",
        label: "Go to Tasks",
        path: `${tenantBase}/tasks`,
        navigate,
        description: `${tenantBase}/tasks`,
        signature: `nav:tasks:${tenantBase}`,
        keywords: ["tasks", "calendar", "deadlines"],
      }),
      commandOpen({
        id: "nav-scout",
        label: "Go to Scout",
        path: `${tenantBase}/scout`,
        navigate,
        description: `${tenantBase}/scout`,
        signature: `nav:scout:${tenantBase}`,
        keywords: ["scout", "ai", "assistant", "chat"],
      }),
      commandOpen({
        id: "nav-insights",
        label: "Go to Insights",
        path: `${tenantBase}/insights`,
        navigate,
        description: `${tenantBase}/insights`,
        signature: `nav:insights:${tenantBase}`,
        keywords: ["insights", "benchmarks", "compliance"],
      }),
      commandOpen({
        id: "nav-facilities",
        label: "Go to Facilities",
        path: `${tenantBase}/facilities`,
        navigate,
        description: `${tenantBase}/facilities`,
        signature: `nav:facilities:${tenantBase}`,
        keywords: ["facilities", "sites", "locations"],
      }),
      commandOpen({
        id: "nav-contacts",
        label: "Go to Contacts",
        path: `${tenantBase}/contacts`,
        navigate,
        description: `${tenantBase}/contacts`,
        signature: `nav:contacts:${tenantBase}`,
        keywords: ["contacts", "people"],
      }),
      commandOpen({
        id: "nav-documents",
        label: "Go to Documents",
        path: `${tenantBase}/documents`,
        navigate,
        description: `${tenantBase}/documents`,
        signature: `nav:documents:${tenantBase}`,
        keywords: ["documents", "files", "uploads"],
      }),
      commandOpen({
        id: "nav-chemicals-overview",
        label: "Go to Chemicals overview",
        path: `${tenantBase}/chemicals/overview`,
        navigate,
        description: `${tenantBase}/chemicals/overview`,
        signature: `nav:chemicals-overview:${tenantBase}`,
        keywords: ["chemicals", "overview", "tier ii", "tierii", "epcra"],
      }),
      commandOpen({
        id: "nav-chemicals-inventory",
        label: "Go to Chemical inventory",
        path: `${tenantBase}/chemicals/inventory`,
        navigate,
        description: `${tenantBase}/chemicals/inventory`,
        signature: `nav:chemicals-inventory:${tenantBase}`,
        keywords: ["chemicals", "inventory", "tier ii"],
      }),
      commandOpen({
        id: "nav-chemicals-catalog",
        label: "Go to Chemical catalog",
        path: `${tenantBase}/chemicals/catalog/chemicals`,
        navigate,
        description: `${tenantBase}/chemicals/catalog/chemicals`,
        signature: `nav:chemicals-catalog:${tenantBase}`,
        keywords: ["chemicals", "catalog"],
      }),
      commandOpen({
        id: "nav-waste-overview",
        label: "Go to Waste overview",
        path: `${tenantBase}/waste/overview`,
        navigate,
        description: `${tenantBase}/waste/overview`,
        signature: `nav:waste-overview:${tenantBase}`,
        keywords: ["waste", "overview", "rcra"],
      }),
      commandOpen({
        id: "nav-waste-manifests",
        label: "Go to Waste manifests",
        path: `${tenantBase}/waste/manifests`,
        navigate,
        description: `${tenantBase}/waste/manifests`,
        signature: `nav:waste-manifests:${tenantBase}`,
        keywords: ["waste", "manifests"],
      }),
      commandOpen({
        id: "nav-waste-submissions",
        label: "Go to Waste submissions",
        path: `${tenantBase}/waste/submissions`,
        navigate,
        description: `${tenantBase}/waste/submissions`,
        signature: `nav:waste-submissions:${tenantBase}`,
        keywords: ["waste", "submissions"],
      })
    );

    return items;
  }, [derived.encodedTenantId, navigate]);

  useRegisterCommands(customerNavCmds, [customerNavCmds], "customer-nav");

  // Tenant default commands
  const tenantCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (derived.tenantId && derived.encodedTenantId) {
      const tenantPath = `/o/${derived.encodedTenantId}`;
      const fullTenantUrl = `${getAppBaseUrl()}${tenantPath}`;
      const staffImplementationPath = `/staff/implementation/${derived.encodedTenantId}`;
      items.push(
        {
          id: "copy-tenant-link",
          label: `Copy link to tenant ${tenantLabel}`,
          description: tenantLabel,
          type: "copy-link",
          keywords: ["tenant", "link", "url", "copy"],
          signature: `tenant-link:${tenantPath}:${tenantLabel}`,
          run: () => copyRichLink(fullTenantUrl, tenantLabel),
        },
        commandCopy({
          id: "copy-tenant-url",
          idKind: "tenant URL",
          value: fullTenantUrl,
          copy: copyToClipboard,
          description: tenantPath,
          signature: `tenant-url:${tenantPath}`,
          keywords: ["tenant", "url", "copy"],
        }),
        commandCopy({
          id: "copy-tenant-id",
          idKind: "tenant ID",
          value: derived.tenantId ?? "",
          copy: copyToClipboard,
          signature: `tenant-id:${derived.tenantId ?? ""}`,
          keywords: ["tenant", "id", "copy"],
        }),
        commandOpen({
          id: "open-staff-implementation",
          label: "Open staff implementation for tenant",
          path: staffImplementationPath,
          navigate,
          description: staffImplementationPath,
          signature: `staff-implementation:${staffImplementationPath}`,
          keywords: ["staff", "implementation", "tenant"],
        })
      );

      if (!derived.facilityId && !derived.reportId) {
        const reportsBase = `/o/${derived.encodedTenantId}/chemicals/reports`;
        const reportsOmnisearch = encodeURIComponent(`reportingYear:${currentTierIIReportingYear}`);
        const reportsPath = `${reportsBase}?omnisearch=${reportsOmnisearch}`;
        items.push(
          commandOpen({
            id: "open-tenant-reports-current-year",
            label: "Open current year chemical reports",
            path: reportsPath,
            navigate,
            description: reportsPath,
            signature: `tenant-reports:${reportsBase}?${reportsOmnisearch}`,
            keywords: ["tenant", "reports", "chemical", "current year"],
          }),
          commandOpen({
            id: "open-waste-overview",
            label: "Open waste overview",
            path: `/o/${derived.encodedTenantId}/waste/overview`,
            navigate,
            description: `/o/${derived.encodedTenantId}/waste/overview`,
            signature: `tenant-waste-overview:/o/${derived.encodedTenantId}/waste/overview`,
            keywords: ["tenant", "waste", "overview"],
          })
        );
      }
    }
    return items;
  }, [
    derived.tenantId,
    derived.encodedTenantId,
    derived.facilityId,
    derived.reportId,
    tenantLabel,
    navigate,
    getAppBaseUrl,
    copyToClipboard,
    copyRichLink,
  ]);

  useRegisterCommands(tenantCmds, [tenantCmds], "default-tenant");

  // Facility default commands
  const facilityCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (derived.encodedTenantId && derived.encodedFacilityId && derived.facilityId) {
      const facilityProfilePath = `/o/${derived.encodedTenantId}/facilities/${derived.encodedFacilityId}/profile`;
      const facilityDisplayLabel = derived.facilityDisplay ?? String(derived.facilityId);
      const facilityProfileUrl = `${getAppBaseUrl()}${facilityProfilePath}`;
      const inventoryPath = `/o/${derived.encodedTenantId}/chemicals/inventory/${derived.encodedFacilityId}`;
      const wastePath = `/o/${derived.encodedTenantId}/waste/facilities/${derived.encodedFacilityId}`;

      items.push(
        {
          id: "copy-facility-link",
          label: `Copy link to facility ${facilityDisplayLabel}`,
          description: facilityDisplayLabel,
          type: "copy-link",
          keywords: ["facility", "link", "url", "copy"],
          signature: `facility-link:${facilityProfilePath}:${facilityDisplayLabel}`,
          run: () => copyRichLink(facilityProfileUrl, facilityDisplayLabel),
        },
        commandCopy({
          id: "copy-facility-url",
          idKind: "facility URL",
          value: facilityProfileUrl,
          copy: copyToClipboard,
          description: facilityProfilePath,
          signature: `facility-url:${facilityProfilePath}`,
          keywords: ["facility", "url", "copy"],
        }),
        commandCopy({
          id: "copy-facility-id",
          idKind: "facility ID",
          value: derived.facilityId,
          copy: copyToClipboard,
          signature: `facility-id:${derived.facilityId ?? ""}`,
          keywords: ["facility", "id", "copy"],
        }),
        commandOpen({
          id: "open-facility-profile",
          label: "Open facility profile",
          path: facilityProfilePath,
          navigate,
          description: facilityProfilePath,
          signature: `facility-profile:${facilityProfilePath}`,
          keywords: ["facility", "profile", "open"],
        }),
        commandOpen({
          id: "open-facility-inventory",
          label: "Open facility chemical inventory",
          path: inventoryPath,
          navigate,
          description: inventoryPath,
          signature: `facility-inventory:${inventoryPath}`,
          keywords: ["facility", "inventory", "chemical", "open"],
        }),
        commandOpen({
          id: "open-facility-waste",
          label: "Open facility waste",
          path: wastePath,
          navigate,
          description: wastePath,
          signature: `facility-waste:${wastePath}`,
          keywords: ["facility", "waste", "open"],
        })
      );

      const customerReportsPath = `/o/${derived.encodedTenantId}/chemicals/reports`;
      const customerOmnisearch = encodeURIComponent(
        `facilityId:${derived.facilityId} reportingYear:${currentTierIIReportingYear}`
      );
      const customerReportsFullPath = `${customerReportsPath}?omnisearch=${customerOmnisearch}`;
      items.push(
        commandOpen({
          id: "open-customer-reports-facility",
          label: "Open current year chemical reports for facility",
          path: customerReportsFullPath,
          navigate,
          description: customerReportsFullPath,
          signature: `facility-reports:${customerReportsPath}?${customerOmnisearch}`,
          keywords: ["facility", "reports", "chemical", "current year", "open"],
        })
      );

      if (!derived.reportId) {
        const staffFacilityPath = `/staff/fulfillment/f/${derived.encodedFacilityId}/${currentTierIIReportingYear}`;
        items.push(
          commandOpen({
            id: "open-staff-fulfillment-facility",
            label: "Open current year chemical report staff fulfillment",
            path: staffFacilityPath,
            navigate,
            description: staffFacilityPath,
            signature: `staff-facility-fulfillment:${staffFacilityPath}`,
            keywords: ["facility", "fulfillment", "staff", "chemical report", "open"],
          })
        );
      }
    }
    return items;
  }, [
    derived.encodedTenantId,
    derived.encodedFacilityId,
    derived.facilityId,
    derived.facilityDisplay,
    derived.reportId,
    navigate,
    getAppBaseUrl,
    copyToClipboard,
    copyRichLink,
  ]);

  useRegisterCommands(facilityCmds, [facilityCmds], "default-facility");

  // Report default commands
  const reportCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    if (derived.encodedTenantId && derived.encodedReportId && derived.reportId) {
      const reportStepperPath = `/o/${derived.encodedTenantId}/chemicals/reports/${derived.encodedReportId}`;
      items.push(
        commandOpen({
          id: "open-report-stepper",
          label: "Open chemical report",
          path: reportStepperPath,
          navigate,
          description: reportStepperPath,
          signature: `report-stepper:${reportStepperPath}`,
          keywords: ["report", "chemical", "open", "stepper"],
        }),
        commandCopy({
          id: "copy-report-id",
          idKind: "report ID",
          value: derived.reportId,
          copy: copyToClipboard,
          signature: `report-id:${derived.reportId ?? ""}`,
          keywords: ["report", "id", "copy"],
        })
      );
    }
    if (derived.encodedReportId) {
      const fulfillmentReportPath = `/staff/fulfillment/r/${derived.encodedReportId}`;
      items.push(
        commandOpen({
          id: "open-report-fulfillment",
          label: "Open chemical report staff fulfillment",
          path: fulfillmentReportPath,
          navigate,
          description: fulfillmentReportPath,
          signature: `report-fulfillment:${fulfillmentReportPath}`,
          keywords: ["report", "fulfillment", "staff", "chemical", "open"],
        })
      );
    }
    return items;
  }, [
    derived.encodedTenantId,
    derived.encodedReportId,
    derived.reportId,
    navigate,
    copyToClipboard,
  ]);

  useRegisterCommands(reportCmds, [reportCmds], "default-report");

  // Linear issue creation command (prefilled via URL)
  const envDep = import.meta.env.VITE_ENV ?? "unknown";
  const linearIssueCmds = useMemo<Command[]>(() => {
    const items: Command[] = [];
    const envLabel = envDep;
    const origin = getAppBaseUrl();
    const currentHref = hrefDep;
    const buildFacilityProfileUrl = () => {
      if (!derived.encodedTenantId || !derived.encodedFacilityId) return undefined;
      return `/o/${derived.encodedTenantId}/facilities/${derived.encodedFacilityId}/profile`;
    };
    items.push({
      id: "linear-create-issue",
      label: "Create Linear issue",
      description: "Opens Linear with prefilled description and context",
      icon: <LinearIcon fontSize="small" color="action" />,
      keywords: ["linear", "issue", "bug", "ticket", "create"],
      run: () => {
        // Force start a replay if one isn't already recording
        const sentryReplayUrl = forceStartReplayAndGetLink();

        const href = currentHref;
        const facilityProfile = buildFacilityProfileUrl();
        const facilityProfileFull =
          derived.facilityId && facilityProfile ? `${origin}${facilityProfile}` : undefined;
        const pageTitle =
          typeof document !== "undefined" && document.title ? document.title : undefined;
        const { title: titleBase, description } = templateLinearTicket({
          href,
          envLabel,
          tenantLabel,
          tenantId: derived.tenantId,
          facilityDisplay:
            derived.facilityDisplay ??
            (derived.facilityId ? String(derived.facilityId) : undefined),
          facilityProfileUrl: facilityProfileFull,
          reportId: derived.reportId,
          sentryReplayUrl,
          pageTitle,
        });

        const url = buildLinearNewIssueUrl({ title: titleBase, description });
        if (typeof window !== "undefined") {
          window.open(url, "_blank", "noopener");
        }
      },
      signature: [
        envLabel,
        currentHref,
        tenantLabel ?? "",
        derived.tenantId ?? "",
        derived.facilityDisplay ?? "",
        buildFacilityProfileUrl() ?? "",
        derived.reportId ?? "",
      ].join("|"),
    });
    return items;
  }, [
    derived.encodedFacilityId,
    derived.encodedTenantId,
    derived.facilityDisplay,
    derived.facilityId,
    derived.reportId,
    derived.tenantId,
    getAppBaseUrl,
    tenantLabel,
    // ensure updates on env and URL changes
    envDep,
    hrefDep,
  ]);

  useRegisterCommands(linearIssueCmds, [linearIssueCmds], "default-linear");

  return null;
}
